/**
 * KeyBindings is a service that provides an easy way to create a keyboard binding and assign an action to that binding.
 * The key concepts in using the service are:<br><br>
 * 1.	The service provides a set of valid keys to create a key binding with through the <code>KeyBindings.Keys</code> object.
 * Valid keys are those on a Windows, Linux and Mac keyboard, with the exception of any key that is only accessible via the SHIFT key. <br><br>
 * 2.	To create a binding, the developer will define a key combination as an array of key choices to be passed to the
 * <code>getBinding()</code> API of the service.
 * For example, [<code>KeyBindings.Keys.CTRL</code>, <code>KeyBindings.Keys.S</code>] is the key combination array to define a binding for “Ctrl-S”.
 * The developer does not need to use the key codes of the keys. The framework uses the corresponding key
 * codes internally in the key handling logic.<br><br>
 * 3.	The keys used in the key combination can determine the platform the binding is valid on:<br>
 *      - A key combination that contains the <code>KeyBindings.Keys.CTRL</code> key will work on Windows and the Mac<br>
 *      - A key combination that contain the <code>KeyBindings.Keys.COMMAND</code> key will work only on the Mac<br>
 *      - A key combination that contain the <code>KeyBindings.Keys.WINDOW</code> key will work only on Windows or Linux<br><br>
 * 4. Key combinations on a Windows platform involving the CONTROL key are typically mapped to the COMMAND key on the Mac.
 * To avoid the developer from having to create separate bindings to account for such differences, a special key choice named
 * SYSCTRL was added to the list of valid keys in the <code>KeyBindings.Keys</code> object.  When a binding is made with this key,
 * the framework will automatically create the correct platform-specific binding.  For example, the combination
 * [<code>KeyBindings.Keys.SYSCTRL</code>, <code>KeyBindings.Keys.S</code>]  will map to [Ctrl-S] on Windows and [Cmd-S] on the Mac.<br><br>
 * 5.	The key bindings API has predefined key combinations for common bindings such as cut, copy, paste, undo and redo
 * through the <code>KeyBindings.StandardKeySequences</code> object.   For example, if a developer wants to create a binding for a cut action,
 * he would use the predefined <code>KeyBindings.StandardKeySequences.CUT</code> sequence for the binding instead of having to create a
 * combination with the <code>KeyBindings.Keys.CTRL</code> and <code>KeyBindings.Keys.X</code> keys.<br><br>
 * 6.	The key bindings service allows a developer to create platform-specific bindings on Windows, Mac or Linux if so desired
 * by passing in an optional argument to the <code>getBinding()</code> API.
 * For example, a [Ctrl-R] key binding can be created that works only on Windows
 *
 * @module mw-keybindings/KeyBindings
 *
 * @copyright Copyright 2016 The MathWorks, Inc.
 */

define([
        // dojo
        "dojo/_base/declare",
        "dojo/_base/lang",

        // keybindings
        "mw-keybindings/DefaultKeys",
        "mw-keybindings/StandardKeySequences",
        "mw-keybindings/BindingUtils",
        "mw-keybindings/Binding",
        "mw-keybindings/KeyHandler",
        "mw-keybindings/Platforms",

        // mw
        "mw-actiondataservice/ActionDataService"

    ],
    function (declare,lang,DefaultKeys,StandardKeySequences,BindingUtils,Binding,KeyHandler,
              Platforms, ActionDataService) {

        var keyBindings = declare([], {

            /**
             * Construct the KeyBindings service
             * @param {actionDataService} actionDataService - An instance of an actionDataService to be registered with this service.  It must contain the actions that will be set in
             * the <code>setAction</code> API on the <code>Binding</code> object returned by this service.
             * @instance
             */
            constructor: function (actionDataService) {

                // error check if the actionDataService input arg is of the correct type
                if ( !lang.isObject(actionDataService) || !actionDataService.isInstanceOf(ActionDataService) ){
                    throw new Error("actionDataService passed into KeyBindings constructor must be an instance of mw-actiondataservice/ActionDataService ");
                }

                // store the actionDataService
                this._actionDataService = actionDataService;

                // A map that stores binding objects.  The keys are unique ids (keySequenceIdentifier
                // that are generated out of the arguments.

                this._bindingMap = {};
                this._keyHandler = new KeyHandler(this);
            },

            /**
             * Get the binding associated to a particular key combination
             *
             * @param {array} keyCombination - An array of keys making up a key binding as defined by the <code>KeyBindings.Keys</code> object.
             * The keyCombination can also be a predefined combination of commonly used keyboard shortcuts as defined in the <code>KeyBindings.StandardKeySequences</code> object.
             * @param {object} options - An optional argument that is used to set options for the binding. Currently the only valid option is to set the platform where the binding will be invoked.
             * To set a specific platform, pass in an object with a <code>platform</code> property set to a choice as defined in the <code>KeyBindings.Platform</code> object.
             * If no option arg is passed in, the binding will run on all platforms.
             * @instance
             * @returns {object} a <code>Binding</code> object
             */
            getBinding: function(keyCombination,options) {

                // get the keyBinding data for the given keyCombination. if there are errors in the processing of
                // the keyCombination exceptions will be thrown
                var keyBindingData = BindingUtils.getKeyBindingData(keyCombination,options);

                // get the keyCombination identifier for the binding
                var keySequenceIdentifier = keyBindingData.keySequenceIdentifier;

                // determine if the binding already exists. if the binding already exists then retrieve it from the map,
                // otherwise create a new binding
                var binding;

                if( this._bindingMap[keySequenceIdentifier] === undefined) {
                    binding = new Binding(keyCombination, options, this._actionDataService);
                    this._bindingMap[keySequenceIdentifier] = binding;
                }

                BindingUtils.validateNoBrowserConflicts(this._bindingMap[keySequenceIdentifier]);

                return this._bindingMap[keySequenceIdentifier];
            },

            /**
             * retrieve the binding from the service with the given KeySequenceIdentifier
             * @param KeySequenceIdentifier
             * @returns {object} The binding object
             *
             * @private
             */
            getBindingFromKeySequenceIdentifier: function(KeySequenceIdentifier) {
                return this._bindingMap[KeySequenceIdentifier];
            },

            /**
             * Remove the binding the KeyBindings service.  The actions associated to that binding will no longer be invoked via the service.
             * @param {object} binding - An instance of the binding object to remove
             * @instance
             */
            removeBinding: function(binding) {

                // error check if the binding input arg is of the correct type
                if ( !lang.isObject(binding) || !binding.isInstanceOf(Binding) ){
                    throw new Error("Binding must be an instance of mw-keybindings/Binding ");
                }

                // get the key sequence identifier for this binding
                var keySequenceIdentifier = binding.getKeySequenceIdentifier();

                // get the binding id for this identifier
                var bindingId = this._bindingMap[keySequenceIdentifier];

                // verify the bindingId is in the map
                if ( bindingId === undefined ) {
                    throw new Error("Error removing the binding: " + binding.getShortcut() +". No such binding exists in the map.");
                }

                // have the binding clean up itself
                binding.cleanup();

                delete this._bindingMap[keySequenceIdentifier];

            },

            /**
             * Destroy the KeyBindings service
             * @instance
             */
            cleanup : function() {
                this._bindingMap = null;
                this._keyHandler.destroy();
            }

        });

        keyBindings.Keys = DefaultKeys;
        keyBindings.StandardKeySequences = StandardKeySequences;
        keyBindings.Platform = Platforms;

        return keyBindings;
    });

